import React from 'react';
import { Color } from 'lib/components/types';
import classes from './index.module.scss';

/**
 * Button variant types
 * - clear: Transparent background with colored text
 * - standard: Solid background with contrasting text
 */
type Variant = 'clear' | 'standard';

/**
 * Button size options
 * - normal: Default size
 * - small: Compact size for tight spaces
 */
type Sizing = 'normal' | 'small';

/**
 * Props interface for the Button component
 * Extends HTML button props with custom styling options
 */
interface Props extends Omit<React.HTMLProps<HTMLButtonElement>, 'start'> {
	/** Text content of the button */
	title: string;
	/** Visual style variant */
	variant?: Variant;
	/** HTML button type */
	type?: 'button' | 'submit';
	/** Whether the button should take full width */
	block?: boolean;
	/** Color theme */
	color?: Color;
	/** Size variant */
	sizing?: Sizing;
	/** Content to display before the title */
	start?: React.ReactNode;
	/** Content to display after the title */
	end?: React.ReactNode;
	/** Whether the button should have active primary styling */
	isActivePrimary?: boolean;
}

/**
 * Mapping of variant types to CSS class names
 */
const classVariantMapping: Record<Variant, string> = {
	clear: 'button-clear',
	standard: 'button-standard',
};

/**
 * Button component
 * Renders a customizable button with various styling options
 * Supports different variants, colors, sizes, and content positioning
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
const Button = ({
	title,
	variant = 'clear',
	className,
	block,
	color = 'primary',
	sizing = 'normal',
	start,
	end,
	isActivePrimary,
	...props
}: Props) => {
	// Override color for disabled buttons
	if (props.disabled) {
		color = 'dark';
	}

	// Process class names
	const className_ = className ? className : '';
	// Add block class if button should take full width
	const blockClass = block ? classes['button-block'] : '';

	// Get base button class from variant
	const buttonClass = classVariantMapping[variant];
	// Add sizing class based on size prop
	const sizingClass = sizing ? classes[`${buttonClass}-sizing-${sizing}`] : '';
	// Add color class based on color prop
	const colorClass = color ? classes[`${buttonClass}-color-${color}`] : '';
	// Add active primary class if specified
	const isActivePrimaryClass = isActivePrimary ? classes[`button-isActivePrimary`] : '';
	// Combine all classes
	const combinedClassNames = `${classes['button']} ${classes[buttonClass]} ${blockClass} ${sizingClass} ${className_} ${colorClass} ${isActivePrimaryClass}`;

	return (
		<button className={combinedClassNames} {...props}>
			{/* Content before the title */}
			{start && <span className={classes['button-start']}>{start}</span>}
			{/* Button title */}
			{title}
			{/* Content after the title */}
			{end && <span className={classes['button-end']}>{end}</span>}
		</button>
	);
};

export default Button;
